import { Injectable, signal, computed } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { InvoiceSearchParams } from '../models/invoice-search.model'
import { Invoice } from '../models/invoice.entity'
import {
  InvoicesListingData,
  InvoicesListingPage,
} from '../models/invoice-listing.model'
import { InvoicesService } from '../invoices.service'
import { InvoicesRepository } from '../invoices.repository'
import { CarriersService } from '../../carriers'
import { CurrenciesService } from '../../currencies'
import { PaymentsService } from '../../payments'
import { Observable } from 'rxjs'
import { mergeById } from '../../../libs/filter.lib'
import { ChannelsService } from '../../channels'

interface InvoicesListingState {
  filters?: InvoiceSearchParams
  data?: Invoice[]
  totalCount?: number
  error?: HttpErrorResponse
  isLoading: boolean
  isInitialized: boolean
  extData?: InvoicesListingData
  dropdownOptions?: InvoicesListingData
  searchString?: string
  searchKeys?: InvoiceSearchParams
  customFilters?: InvoiceSearchParams
}

const INVOICES_LISTING_INITIAL_STATE: InvoicesListingState = {
  isLoading: false,
  isInitialized: false,
}

@Injectable()
export class InvoicesListingStore {
  constructor(
    private invoicesService: InvoicesService,
    private invoicesRepository: InvoicesRepository,
    private carriersService: CarriersService,
    private channelsService: ChannelsService,
    private currenciesService: CurrenciesService,
    private paymentsService: PaymentsService,
  ) {}

  private state = signal<InvoicesListingState>(INVOICES_LISTING_INITIAL_STATE)

  // Selectors

  readonly isInitialized = computed(() => this.state().isInitialized)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly filters = computed(() => this.state().filters)
  readonly isFiltered = computed(() => {
    const filters = this.state().filters
    return !!filters && Object.keys(filters).length > 0
  })
  readonly isErrored = computed(() => !!this.state().error)
  readonly invoices = computed(() => this.state().data || [])
  readonly extData = computed(() => this.state().extData)
  readonly dropdownOptions = computed(() => this.state().dropdownOptions)
  readonly totalCount = computed(() => this.state().totalCount || 0)
  readonly searchKeys = computed(() => this.state().searchKeys)

  // Effects

  async search(
    params?: InvoiceSearchParams,
    returnAll?: boolean,
  ): Promise<void> {
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.invoicesRepository
        .searchInvoices$(
          this.parseStateFilterParams(this.state(), params),
          this.state().extData,
          returnAll,
        )
        .subscribe({
          next: (data) => {
            resolve()
            this.setData(data)
          },
          error: (error) => {
            reject(error)
            this.setError(error)
          },
        })
    })
  }

  async delete(invoice: Invoice): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.invoicesRepository
        .alert('INVOICE_DELETION', invoice)
        .then((response) => {
          if (response === 'success') {
            this.setIsLoading(true)
            this.invoicesService.delete$(invoice._id).subscribe({
              next: () => {
                resolve(true)
                this.invoicesRepository.notify({
                  code: 'INVOICE_DELETED',
                })
              },
              error: (error: HttpErrorResponse) => {
                resolve(false)
                this.setError(error)
              },
            })
          }
        })
    })
  }

  async searchLookup(event: {
    dataSourceKey: string
    q?: string
    dataKeyField?: string
    dataDisplayField?: string
    offset?: number
    limit?: number
  }): Promise<void> {
    type LookupKey = keyof InvoicesListingData
    type ArrayElement<T> = T extends (infer U)[] ? U : never
    type LookupValue = ArrayElement<NonNullable<InvoicesListingData[LookupKey]>>

    const queryKey = (event.dataDisplayField ?? 'name') + ':ct'
    const offset = event.offset ?? 0
    const limit = event.limit ?? 50
    const query = {
      offset,
      limit,
      ...(event.q ? { [queryKey]: event.q } : {}),
    } as Record<string, unknown>

    const serviceMap: Partial<
      Record<LookupKey, () => Observable<LookupValue[]>>
    > = {
      carriers: () => this.carriersService.list$(query),
      channels: () => this.channelsService.list$(query),
      currencies: () => this.currenciesService.list$(query),
      payments: () => this.paymentsService.list$(query),
    }

    const search$ = serviceMap[event.dataSourceKey as LookupKey]
    if (!search$) return

    search$().subscribe((items) => {
      this.state.update((s) => {
        const key = event.dataSourceKey as LookupKey
        const prev = (s.dropdownOptions?.[key] ?? []) as { _id: string }[]
        const incoming = items as { _id: string }[]
        const merged = prev.length > 0 ? mergeById(prev, incoming) : incoming
        return {
          ...s,
          dropdownOptions: {
            ...s.extData,
            [event.dataSourceKey]: merged as LookupValue[],
          },
        }
      })
    })
  }

  // Reducers

  setData(data: InvoicesListingPage) {
    const extData = this.extData()
    const newExtData = {
      carriers: mergeById(extData?.carriers, data.extData?.carriers),
      channels: mergeById(extData?.channels, data.extData?.channels),
      currencies: extData?.currencies,
      payments: mergeById(extData?.payments, data.extData?.payments),
    }
    this.state.set({
      ...this.state(),
      data: data.data,
      totalCount: data.totalCount,
      extData: newExtData,
      dropdownOptions: newExtData,
      isInitialized: true,
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setFilters(filters?: InvoiceSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...filters },
    })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }

  resetLookups() {
    this.state.set({
      ...this.state(),
      dropdownOptions: undefined,
      extData: undefined,
    })
  }

  addFilter(filter: InvoiceSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...this.state().filters, ...filter },
    })
  }

  removeFilter(filter: InvoiceSearchParams) {
    const currentFilters = this.state().filters
    if (currentFilters) {
      // Create a new filters object without the specified filter properties
      const updatedFilters = { ...currentFilters }

      // Remove each property specified in the filter parameter
      for (const key of Object.keys(filter)) {
        if (key in updatedFilters) {
          delete updatedFilters[key as keyof InvoiceSearchParams]
        }
      }

      this.state.set({
        ...this.state(),
        isInitialized: true,
        filters: updatedFilters,
      })
    }
  }

  setSearchKeys(searchKeys?: InvoiceSearchParams) {
    this.state.set({ ...this.state(), searchKeys })
  }

  resetFilters() {
    this.state.set({
      ...this.state(),
      filters: {},
    })
  }

  private parseStateFilterParams(
    state: InvoicesListingState,
    filters?: InvoiceSearchParams,
  ): InvoiceSearchParams {
    let params: InvoiceSearchParams = filters || {}
    if (state.searchString) {
      params.q = state.searchString
    }
    if (state.customFilters) {
      params = { ...params, ...state.customFilters }
    }
    if (state.searchKeys) {
      params = { ...params, ...state.searchKeys }
    }

    return params
  }
}
